import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { KeyValues, Notice } from "../../../design/components";
import { ApiError } from "../../../api/client";
import { useDocument, useDocumentEvents } from "../../../api/queries";
import { dateTime, omr } from "../../../lib/format";
import type { DocumentDetail } from "../../../api/types";
import { ArchivePanel } from "./ArchivePanel";
import { EventsPanel } from "./EventsPanel";
import { Header } from "./Header";
import { OutcomePanel } from "./OutcomePanel";
import { PayloadTabs } from "./PayloadTabs";
import { StagePanel } from "./StagePanel";
import { ValidationPanel } from "./ValidationPanel";

function amount(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : omr(Number(value));
}

function DetailsPanel({ doc }: { doc: DocumentDetail }) {
  const { t } = useTranslation();
  const totals = doc.totals;
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="grow">{t("documents.inspector.details.title", "Details")}</h2>
      </div>
      <div className="card-body">
        <KeyValues
          pairs={[
            [t("documents.inspector.details.counterparty", "Counterparty"), doc.counterparty ?? "—"],
            [t("documents.inspector.details.sellerVat", "Seller VAT"), doc.sellerVat ?? "—"],
            [
              t("documents.inspector.details.senderParticipant", "Sender participant"),
              <span className="mono">{doc.participantFrom ?? "—"}</span>,
            ],
            [
              t("documents.inspector.details.receiverParticipant", "Receiver participant"),
              <span className="mono">{doc.participantTo ?? "—"}</span>,
            ],
            [t("documents.inspector.details.taxableAmount", "Taxable amount"), amount(totals?.taxExclusive)],
            [t("documents.inspector.details.vat", "VAT"), amount(totals?.taxAmount)],
            [
              t("documents.inspector.details.payable", "Payable"),
              `${amount(totals?.payable)} ${doc.currency ?? ""}`.trim(),
            ],
            [t("documents.inspector.details.created", "Created"), dateTime(doc.createdAt)],
            [t("documents.inspector.details.updated", "Updated"), dateTime(doc.updatedAt)],
          ]}
        />
      </div>
    </section>
  );
}

export function DocumentInspector() {
  const { t } = useTranslation();
  const { docId } = useParams();
  const document = useDocument(docId);
  // a 404 detail must not trigger the sibling requests
  const events = useDocumentEvents(docId, document.isSuccess);

  if (document.isError) {
    const missing = document.error instanceof ApiError && document.error.status === 404;
    return (
      <Notice kind={missing ? "info" : "fail"}>
        <div>
          {missing
            ? t("documents.inspector.notAvailable", "This document is not available")
            : t("documents.inspector.loadError", "This document could not be loaded.")}
        </div>
        <Link className="link" to="/documents">
          {t("documents.inspector.backToDocuments", "Back to documents")}
        </Link>
      </Notice>
    );
  }

  const doc = document.data;
  if (!doc || !docId) return <div className="page-loading" />;

  return (
    <>
      <Header doc={doc} />
      <div className="split">
        <div className="grid">
          <StagePanel doc={doc} events={events.data ?? []} />
          <PayloadTabs docId={docId} artifacts={doc.artifacts} />
          <ValidationPanel docId={docId} />
          <EventsPanel docId={docId} />
        </div>
        <div className="grid">
          <OutcomePanel docId={docId} />
          <DetailsPanel doc={doc} />
          <ArchivePanel doc={doc} />
        </div>
      </div>
    </>
  );
}
